/**
 * Which pages are drawn as a desk with sheets on it, rather than on plain white.
 *
 * The job feeds — the home page, /jobs, the group wall and /search — tint the page itself
 * (`var(--card)`, #f8fafc in light) and lay white cards (`var(--paper)`) on top, so a card
 * reads as a separate object instead of dissolving into the page behind it. Everywhere else
 * is still a white page.
 *
 * <b>This list exists because the header is not part of the page.</b> It is a sibling of the
 * routed content under Layout, so it cannot inherit that tint — Layout paints the strip of
 * <main> that the page's own background does not reach (the padding under the bottom nav),
 * and the header asks isDeskHeader below. Adding a page to the list is therefore only half
 * the job — the page still has to paint its own background.
 *
 * @param {string} pathname from useLocation
 * @returns {boolean}
 */
export const isDeskPage = (pathname) =>
  pathname === "/" || pathname === "/jobs" || pathname === "/job-group"
  || pathname === "/search";

/**
 * Whether the header paints the desk tint as well as the page under it.
 *
 * It normally has to: left white above a tinted page it drew a pale band across the top of
 * the screen that read as two backgrounds meeting. /search is the exception — its bar is the
 * search field, the back button and the filter, and it stays white so that row reads as
 * chrome rather than as the top of the results.
 *
 * @param {string} pathname from useLocation
 * @returns {boolean}
 */
export const isDeskHeader = (pathname) => isDeskPage(pathname) && pathname !== "/search";

/** The desk itself — the tint a desk page paints, and the header above it. */
export const DESK_BACKGROUND = "var(--card, #F8FAFC)";

/**
 * The desk as classes: tinted on a phone, plain white from `md` (1024px) up.
 *
 * <b>The tint is a mobile treatment.</b> On a phone the feed runs edge to edge, so without
 * it a card has nothing to sit on and dissolves into the page. The desktop layout already
 * separates things structurally — the list is a bordered, rounded box inside a padded
 * page — so tinting there only turned the whole window grey, which is not what that layout
 * ever looked like.
 *
 * Classes rather than an inline style because a media query is the whole point, and
 * `style={}` cannot hold one.
 */
export const DESK_SURFACE_CLASS = "bg-[var(--card,#F8FAFC)] md:bg-[var(--bg,#fff)]";

/** A sheet on the desk — what a card on one of those pages is filled with. */
export const SHEET_BACKGROUND = "var(--paper, #fff)";
